import { useMemo, useRef } from 'react'
import { Box, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import { FA_COLOR, FA_ORDER, fmtTime, type EmotionKey } from '@/components/researcher/liveDemo/constants'
import type { EmotionSeries } from '@/services/emotionAnalysisService'

const strokeFor = (k: EmotionKey) => (k === 'happy' || k === 'surprised' ? 3.4 : 2.6)
const opacityFor = (k: EmotionKey) => (k === 'happy' || k === 'surprised' ? 1 : 0.9)

/**
 * Full-stimulus emotion timeline with a synced playhead. Mirrors the live-demo
 * VideoTimeline visual language, but driven by a precomputed grid + series
 * (aggregate average or a single respondent's curve) rather than live samples.
 */
export default function EmotionTimeline({
  series,
  gridMs,
  durationMs,
  currentMs,
  onScrub,
  segments,
}: {
  series: EmotionSeries
  gridMs: number[]
  durationMs: number
  currentMs: number
  onScrub?: (ms: number) => void
  /** Merged "all stimuli" mode: clip boundaries to mark + label along the axis. */
  segments?: { start_ms: number; title: string }[]
}) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('liveDemo')

  const W = 880, H = 220, padL = 36, padR = 12, padT = 14, padB = 26
  const innerW = W - padL - padR, innerH = H - padT - padB
  const dur = durationMs || 1
  const n = gridMs.length
  const x = (ms: number) => padL + (ms / dur) * innerW
  const y = (v: number) => padT + (1 - v) * innerH
  const hasData = n >= 2

  const ticks = 6

  // The curves, gridlines and axis labels depend only on the data/geometry — not
  // on currentMs — so memoize them. Otherwise every playhead tick (~30 Hz during
  // playback) would rebuild all 7 SVG paths, which is the bulk of the per-frame
  // cost; now a tick only moves the cheap playhead below.
  const paths = useMemo(() => {
    if (!hasData) return []
    // Break the line at gaps (null grid points) so the curve begins at the first
    // real frame instead of a flat held lead-in, and stops at the last frame
    // before a no-face dropout instead of spanning it: each contiguous run of
    // covered points becomes its own M…L… subpath.
    return FA_ORDER.map((k) => {
      const vals = series[k] || []
      let d = ''
      let run = 0 // points in the run being drawn
      let last = ''
      const closeRun = () => {
        // A lone covered point is a moveto with no lineto, which draws nothing;
        // repeat it so the round linecap renders the sample as a dot.
        if (run === 1) d += `L${last} `
        run = 0
      }
      gridMs.forEach((ms, i) => {
        const v = vals[i]
        if (v == null || !Number.isFinite(v)) {
          closeRun()
          return
        }
        last = `${x(ms).toFixed(1)} ${y(v).toFixed(1)}`
        d += `${run === 0 ? 'M' : 'L'}${last} `
        run += 1
      })
      closeRun()
      return { key: k, d: d.trim() }
    })
    // x/y are pure functions of the geometry constants captured below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [series, gridMs, hasData, dur])

  const playX = x(Math.min(currentMs, dur))

  const dragging = useRef(false)

  const scrubFromEvent = (e: React.PointerEvent<SVGSVGElement>) => {
    if (!onScrub || !hasData) return
    const rect = e.currentTarget.getBoundingClientRect()
    const px = ((e.clientX - rect.left) / rect.width) * W
    const frac = Math.max(0, Math.min(1, (px - padL) / innerW))
    onScrub(frac * dur)
  }

  const handlePointerDown = (e: React.PointerEvent<SVGSVGElement>) => {
    if (!onScrub || !hasData) return
    dragging.current = true
    e.currentTarget.setPointerCapture(e.pointerId)
    scrubFromEvent(e)
  }

  const handlePointerMove = (e: React.PointerEvent<SVGSVGElement>) => {
    if (!dragging.current) return
    e.preventDefault()
    scrubFromEvent(e)
  }

  const endDrag = (e: React.PointerEvent<SVGSVGElement>) => {
    if (!dragging.current) return
    dragging.current = false
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId)
  }

  return (
    <Box>
      <svg
        width="100%"
        viewBox={`0 0 ${W} ${H}`}
        style={{
          display: 'block',
          minHeight: H,
          touchAction: 'none',
          cursor: onScrub && hasData ? (dragging.current ? 'grabbing' : 'pointer') : 'default',
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        {[0, 0.5, 1].map((g) => (
          <g key={g}>
            <line x1={padL} y1={y(g)} x2={W - padR} y2={y(g)} stroke={c.ink08} />
            <text x={padL - 6} y={y(g) + 3} textAnchor="end" fontFamily="Lato" fontSize="10" fill={c.ink48}>
              {Math.round(g * 100)}%
            </text>
          </g>
        ))}
        {/* Merged mode: shade alternate clips + mark/label each boundary so the
            stitched curve still reads as a sequence of distinct stimuli. */}
        {(segments?.length ?? 0) > 1 &&
          segments!.map((seg, i) => {
            const start = seg.start_ms
            const end = i + 1 < segments!.length ? segments![i + 1].start_ms : dur
            const x0 = x(start)
            const segW = x(end) - x0
            const chars = Math.max(0, Math.floor((segW - 8) / 5.6))
            const label = seg.title.length > chars ? `${seg.title.slice(0, Math.max(0, chars - 1))}…` : seg.title
            return (
              <g key={`${seg.start_ms}-${i}`}>
                {i % 2 === 1 && (
                  <rect x={x0} y={padT} width={segW} height={innerH} fill={c.ink} opacity={0.025} />
                )}
                {i > 0 && (
                  <line x1={x0} y1={padT} x2={x0} y2={H - padB} stroke={c.ink08} strokeWidth={1} />
                )}
                {chars >= 2 && (
                  <text x={x0 + 4} y={padT + 9} fontFamily="Lato" fontSize="9.5" fontWeight="700" fill={c.ink48}>
                    {label}
                  </text>
                )}
              </g>
            )
          })}
        {Array.from({ length: ticks + 1 }).map((_, i) => (
          <text
            key={i}
            x={padL + (i / ticks) * innerW}
            y={H - padB + 16}
            textAnchor="middle"
            fontFamily="Lato"
            fontSize="10"
            fill={c.ink56}
            style={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {fmtTime(((dur / ticks) * i) / 1000)}
          </text>
        ))}
        {!hasData && (
          <text x={W / 2} y={H / 2} textAnchor="middle" fontFamily="Lato" fontSize="13" fill={c.ink48}>
            {t('videoTimeline.noData')}
          </text>
        )}
        {paths.map(({ key, d }) => (
          <path
            key={key}
            d={d}
            fill="none"
            stroke={FA_COLOR[key]}
            strokeWidth={strokeFor(key)}
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity={opacityFor(key)}
          />
        ))}
        {hasData && (
          <>
            <line x1={playX} y1={padT} x2={playX} y2={H - padB} stroke={c.ink} strokeWidth="1.5" strokeDasharray="3 3" />
            <polygon points={`${playX - 6},${padT - 4} ${playX + 6},${padT - 4} ${playX},${padT + 4}`} fill={c.ink} />
          </>
        )}
      </svg>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 0.5 }}>
        {FA_ORDER.map((k) => (
          <Typography
            key={k}
            component="span"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, fontSize: 12.5, color: c.ink72 }}
          >
            <Box component="span" sx={{ width: 11, height: 11, borderRadius: '3px', background: FA_COLOR[k] }} />
            {t(`emotions.${k}`)}
          </Typography>
        ))}
      </Box>
    </Box>
  )
}
